import * as XLSX from 'xlsx';

/**
 * Build a one-sheet .xlsx with the given headers and a single sample row, and trigger a download.
 * `sample` is keyed by header text.
 */
export function downloadTemplate(filename: string, headers: string[], sample: Record<string, string>) {
  const ws = XLSX.utils.json_to_sheet([sample], { header: headers });
  // Give the columns a sensible width so headers aren't clipped.
  ws['!cols'] = headers.map(h => ({ wch: Math.max(16, h.length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, filename);
}

/**
 * Export a list of rows (objects keyed by display header) to a .xlsx download. Column order
 * follows the keys of the first row.
 */
export function downloadRows(filename: string, rows: Record<string, string | number>[]) {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
  ws['!cols'] = headers.map(h => ({ wch: Math.max(14, h.length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, filename);
}

/**
 * Export rows to a printable PDF by opening a styled table in a new window and triggering the
 * browser's print dialog ("Save as PDF"). Dependency-free — no PDF library needed.
 */
export function exportRowsAsPdf(title: string, rows: Record<string, string | number>[]) {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
  const html =
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>` +
    `<style>body{font-family:system-ui,Segoe UI,sans-serif;padding:24px;color:#111}` +
    `h1{font-size:18px;margin:0 0 12px}table{border-collapse:collapse;width:100%;font-size:12px}` +
    `th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}th{background:#f3f4f6}</style></head>` +
    `<body><h1>${esc(title)}</h1>` +
    `<table><thead><tr>${headers.map(h => `<th>${esc(h)}</th>`).join('')}</tr></thead>` +
    `<tbody>${rows.map(r => `<tr>${headers.map(h => `<td>${esc(r[h])}</td>`).join('')}</tr>`).join('')}</tbody>` +
    `</table><script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) { alert('Allow pop-ups to export PDF.'); return; }
  w.document.write(html);
  w.document.close();
}

/** Read the first sheet of an uploaded workbook into an array of row objects keyed by header text. */
export function readSheetRows(file: File): Promise<Record<string, unknown>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        // cellDates so date/time-formatted cells come back as JS Dates (not raw serials), letting
        // callers normalise them (see ImportExport). Non-date sheets are unaffected.
        const wb = XLSX.read(data, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        if (!ws) { resolve([]); return; }
        resolve(XLSX.utils.sheet_to_json(ws, { defval: '' }) as Record<string, unknown>[]);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
